/**
 * /admin/models/assignment — per-agent provider+model+temperature
 * defaults AND each model's live health, on one page. (Pre-#1281:
 * `/admin/agents/models`; tab label was "Models", now "Assignments" to
 * disambiguate from the renamed parent tab.)
 *
 * Extracted out of `/admin/config` (#429 Wave 3 Track D / #410) and
 * widened in the Models-UI pass to surface all SIX agents the runtime
 * has (planner, generator, healer, analyzer, auditor, scanner) plus a
 * temperature input per agent and a pricing-catalog-sourced model
 * dropdown instead of free-text provider+model inputs.
 *
 * **#1281 follow-up — config and health live together.** The first cut
 * of #1281 shipped model health on a *separate* "Catalog" page, so an
 * operator configuring an agent's model and an operator checking that
 * the model works were on two different screens. That split is gone:
 * each agent row now carries its own status (Working / Not working /
 * Checking…), a per-row "Check now" button, and the plain-language fix
 * message when the provider rejects it — right next to the controls
 * that set the model. The 20-minute scheduled sweep still updates these
 * statuses in the background; the "Check all now" banner kicks it early
 * after a key rotation. The embedding model — not an agent, so it has
 * no assignment controls — sits as a read-only row at the bottom so its
 * health is visible from the same screen.
 *
 * The original four-agent surface left scanner + analyzer invisible
 * (analyzer's runtime env-var name is the legacy `EXTRACTOR_*`, which
 * is why the resolver maps `extractor` → `analyzer` AppSettings key
 * but the env-var names stay put); operators had no admin path to
 * pin a model for those two. The widened surface fixes that.
 *
 * Why a pricing-catalog dropdown instead of free-text: the catalog
 * (`/api/admin/pricing`) is the single source of truth for which
 * model specs the system knows how to bill. Free-text inputs let an
 * operator pin a typo'd model that would crash agent runs at
 * parseModelSpec time (defense at the PUT validator catches that
 * today, but a dropdown removes the entire failure mode by making
 * typos impossible).
 *
 * Pre-fill semantics: each row's dropdown + temperature input start
 * pre-filled with the RESOLVED current values — what the agent is
 * actually using right now — so the operator can see what's running
 * even when no doc override exists. Pre-filled-but-unedited values
 * are rendered subtly (italic muted) and NEVER persist on save —
 * only operator-edited fields go in the PUT body.
 *
 * Save is page-local: submitting writes ONLY the `agentModels` field
 * group, leaving every other AppSettings field group (features,
 * defaultRunBudgets, emailTemplates) untouched.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import {
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Loader2,
  HelpCircle,
  RefreshCw,
} from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  SearchableSelect,
  type SearchableSelectOption,
  type SearchableSelectFacet,
} from '@/components/ui'
import {
  adminAppSettingsAPI,
  adminModelVerificationAPI,
  adminPricingAPI,
  biAPI,
  type AdminAgentName,
  type AgentModelDefaults,
  type AgentModelOverride,
  type AppSettingsResponse,
  type ModelComparisonAgent,
  type ModelLeaderboardRow,
  type ModelPricingRow,
  type ModelTargetRow,
  type ModelVerificationState,
  type ResolvedAgentEntry,
  type ResolvedAppSettings,
  type RetiredModelSummary,
} from '@/lib/api'
import { AgentsSubNav } from '../AgentsSubNav'
import { Provenance } from '../../components/Provenance'
import {
  overrideToNamePattern,
  parseNamePattern,
  modelPriceStatus,
  modelReadsImages,
  modelCostCategory,
  optionFacets,
  FACET_CURATED,
  FACET_FRONTIER,
  FACET_READS_IMAGES,
  FACET_WORKS_WITH_AGENT,
} from '@/lib/agentModelSpec'
import { CapabilityBadge, reasoningBadge, formatContextWindow } from '@/lib/modelCapabilityBadges'
import { CostCategoryPill } from '@/components/CostCategoryPill'
import { AdvancedSettingsNotice } from '@/components/AdvancedSettingsNotice'
import { retiredByPattern, retiredOptionSuffix } from '@/lib/retiredModelLabel'
import { rankModels, buildRecoInputs, type RankedModel } from '@/lib/modelRecommendation'

const AGENTS: readonly AdminAgentName[] = [
  'planner', 'generator', 'healer', 'analyzer', 'auditor', 'scanner', 'proposer', 'refiner', 'dedupJudge',
]

/** The agents whose output the auditor scores (TQI / PQI / HQI) — the only ones
 * with a per-(agent, model) quality signal, so only they get the full
 * cost↔performance "Help me choose" slider (#1844 Slice 3). The model-comparison
 * endpoint accepts exactly these three; the other four rows show a cost-only
 * note instead of an invented score. */
const SCORED_AGENTS = ['generator', 'planner', 'healer'] as const satisfies readonly ModelComparisonAgent[]
// The scored subset are all AdminAgentNames; `candidate` (proposer/refiner)
// is a ModelComparisonAgent but NOT an admin agent, so the predicate targets
// this narrower type rather than the full ModelComparisonAgent (#2101 3c).
type ScoredAgent = (typeof SCORED_AGENTS)[number]
function isScoredAgent(a: AdminAgentName): a is ScoredAgent {
  return (SCORED_AGENTS as readonly string[]).includes(a)
}

/** Facet chips on the model picker (#1844 Slice 1.5), in display order.
 * SearchableSelect appends its own "All Models" chip. Otto Curated is the
 * default view (see the call site's `defaultFacet`), so an operator opens on
 * a vetted shortlist instead of ~600 rows; the derived "Vision" facet reads
 * the #1842 `imageInput` flag (labeled "Vision" to match the capability
 * badge — #1854). */
/** Facet chips, in display order. `SearchableSelect` appends its own
 * "All Models" chip.
 *
 * #1928 adds the agent-aware chip FIRST, but `Otto Curated` remains the chip
 * active on open (`defaultFacet` below) — the new one is available, not
 * imposed. Consequence worth knowing: because the default view is not
 * capability-filtered, the curated shortlist can still contain a model the
 * agent cannot use, so on the view an operator actually opens the BADGES carry
 * the signal and the save-time warning is the backstop. That is why the badges
 * render on every option rather than only inside this facet.
 *
 * The chips are SINGLE-select, which is why this is one agent-aware chip rather
 * than separate `Tool calling` / `Reasoning` chips: those could each filter
 * alone but never combine, so "tool calling AND vision" would be unaskable. */
function modelFacetsForAgent(
  agent: AdminAgentName,
  requiredCaps: readonly string[],
): readonly SearchableSelectFacet[] {
  // An agent that requires nothing gets no chip. Showing one would imply a
  // distinction that does not exist — every model "works with" the auditor —
  // and its count would equal the whole catalog, which teaches the operator to
  // ignore the chip on the agents where it matters. Same reason it disappears
  // when the server sends no requirement map at all.
  const base: SearchableSelectFacet[] = []
  if (requiredCaps.length > 0) {
    base.push({
      key: FACET_WORKS_WITH_AGENT,
      label: `Works with ${AGENT_LABEL[agent]}`,
      // States what the list IS (confirmed) and what it is not (a verdict on
      // everything else). The chip asserts evidence, not the absence of it:
      // a model we simply have no information about is left out of this list
      // but is neither excluded from use nor implied to be broken.
      hint: `Models we've confirmed can work with the ${AGENT_LABEL[agent].toLowerCase()}. Others are still available under All Models or by searching.`,
    })
  }
  return [
    ...base,
    { key: FACET_CURATED, label: 'Otto Curated' },
    { key: FACET_FRONTIER, label: 'Frontier' },
    { key: FACET_READS_IMAGES, label: 'Vision' },
  ]
}

/** What each agent uses tools FOR, in the operator's words — the second half of
 * the warning's first sentence ("…the healer needs that to fix a test").
 *
 * Per agent rather than one generic "to do its job" because the mockup's
 * wording is binding and it is specific. It also earns its keep: an operator
 * who does not know what the planner is learns it from the warning, at the
 * moment they need to decide something about it.
 *
 * Only the three gated agents can produce this warning; the rest are listed so
 * the record stays exhaustive if a requirement is ever added to one. */
const AGENT_PURPOSE: Record<AdminAgentName, string> = {
  planner: 'explore your app',
  generator: 'write a test',
  healer: 'fix a test',
  analyzer: 'do its job',
  auditor: 'do its job',
  scanner: 'do its job',
  proposer: 'do its job',
  refiner: 'do its job',
  dedupJudge: 'do its job',
}

/** Operator-facing display label for each agent — used for the row title on
 * this page AND the "Works with <Agent>" facet chip, one source so they never
 * disagree. Most are the capitalised name; `dedupJudge` shows as "Deduper"
 * (#2309), which is why the row can't just capitalise the raw key. */
const AGENT_LABEL: Record<AdminAgentName, string> = {
  planner: 'Planner', generator: 'Generator', healer: 'Healer',
  analyzer: 'Analyzer', auditor: 'Auditor', scanner: 'Scanner',
  proposer: 'Proposer', refiner: 'Refiner',
  dedupJudge: 'Deduper',
}

/** The verification target id for an agent row. The backend keys agent
 * targets as `agent:<adminName>` (and uses the admin label `analyzer`,
 * NOT the runtime `extractor`, as the segment) — so the join from a
 * config row to its health is just this prefix, no extractor special-
 * casing on the frontend. See `application/modelVerification/targets.ts`. */
function agentTargetId(agent: AdminAgentName): string {
  return `agent:${agent}`
}

/** Sentinel value the dropdown's first option carries — selecting it
 * clears any doc override for the row and falls through to the env
 * cascade. Empty string lets us bind directly to the `<select>` value
 * without a separate "is-cleared" boolean. */
const CLEAR_OPTION_VALUE = ''

/** Human "N minutes ago" for the last-checked timestamp. Falls back to
 * the absolute date when older than a day. Kept inline so the page
 * renders the right phrasing without a heavier date library in the
 * bundle. */
function relativeTime(iso: string | null): string {
  if (!iso) return ''
  const ts = new Date(iso).getTime()
  if (Number.isNaN(ts)) return ''
  // Clamp to 0 so a future timestamp (client clock behind the server)
  // reads "a moment ago" rather than a misleading large/negative value.
  const seconds = Math.max(0, Math.round((Date.now() - ts) / 1000))
  if (seconds < 60) return 'a moment ago'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  return new Date(iso).toLocaleDateString()
}

function StatusPill({ state }: { state: ModelVerificationState }) {
  switch (state) {
    case 'working':
      return (
        <Badge variant="secondary" className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Verified
        </Badge>
      )
    case 'notWorking':
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Not working
        </Badge>
      )
    case 'checking':
      return (
        <Badge variant="outline" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Checking…
        </Badge>
      )
    case 'notChecked':
      return (
        <Badge variant="outline" className="gap-1 text-muted-foreground">
          <HelpCircle className="h-3 w-3" />
          Not checked yet
        </Badge>
      )
  }
}

/** The verification state a row ACTUALLY renders, after the #1364
 * cache-invalidation downgrades. A saved `working`/`notWorking` is only
 * trustworthy when (a) there is a live API key to call the model with and
 * (b) the model selection has not changed since the check ran — otherwise it
 * is downgraded to `notChecked` so the operator re-verifies against the
 * current configuration instead of trusting a stale badge. While a check is
 * in flight the state is `checking`.
 *
 * This is the ONE definition of that rule: the per-row health block AND the
 * page banner both read health through it, so they can never disagree. The
 * banner used to key off the raw `status.state` and promise a fix note for
 * rows this helper had downgraded — so no note rendered (#2090). The server
 * mirrors this same rule when it computes the shared `anyNotWorking` boolean
 * (see routes/admin/modelVerification.ts); keep the two in sync. */
export function effectiveModelState(
  target: ModelTargetRow,
  checking = false,
): ModelVerificationState {
  if (checking) return 'checking'
  const cached = target.status?.state ?? 'notChecked'
  // A served-locally model (the bundled local embeddings) needs no key, so a
  // null preview is not "unconfigured" for it and must not downgrade the badge.
  const noLiveKey = target.apiKeyPreview === null && !target.servedLocally
  const modelChanged =
    target.status !== null &&
    (target.status.checkedModelId !== target.model ||
      (target.status.checkedProvider ?? null) !== (target.provider ?? null))
  return noLiveKey || modelChanged ? 'notChecked' : cached
}

/**
 * Whether a target will ACTUALLY render a fix note: the effective state above
 * PLUS the note's own `fixMessage` guard. Keying off the raw `status.state`
 * lit the banner for downgraded rows that render no note (#2090).
 *
 * Extracted in #2112 because a third reader appeared. The agent rows collapse,
 * and a failing agent's row opens itself so the banner's promise — "See the fix
 * note under each row" — stays true. If the rows judged failure by a different
 * rule than the banner, we would be back to a banner pointing at a note that
 * is not there, only with the row closed instead of the note missing.
 */
export function isNotWorkingWithFix(target: ModelTargetRow, checking = false): boolean {
  return effectiveModelState(target, checking) === 'notWorking' && !!target.status?.fixMessage
}

/** The inline health block that hangs off each agent row (and the
 * embedding row): the current status pill, last-checked stamp, a
 * per-row "Check now" button, and — when failing — the plain-language
 * fix message naming the env var to look at. The verify target may be
 * absent (verification list still loading or soft-failed); in that case
 * we render nothing rather than a misleading "Not checked yet". */
function ModelHealth({
  target,
  checking,
  error,
  onCheck,
}: {
  target: ModelTargetRow | undefined
  checking: boolean
  error: string | undefined
  onCheck: () => void
}) {
  if (!target) return null
  // The rendered state is the shared #1364 downgrade — see effectiveModelState,
  // which the page banner reads too so they never disagree (#2090). The two
  // locals below re-derive WHY a downgrade happened, only to choose the hint
  // text; both stay suppressed while a check is in flight (the cached
  // checkedProvider / checkedModelId can be stale mid-check, which would wrongly
  // fire the "Model selection changed…" hint over the spinner — Copilot on
  // PR #1365).
  const cachedState = target.status?.state ?? 'notChecked'
  const state = effectiveModelState(target, checking)
  const noLiveKey = !checking && target.apiKeyPreview === null && !target.servedLocally
  const modelChangedSinceCheck =
    !checking &&
    target.status !== null &&
    (target.status.checkedModelId !== target.model ||
      (target.status.checkedProvider ?? null) !== (target.provider ?? null))
  return (
    <div className="space-y-2 pt-1">
      <div className="flex items-center gap-2 flex-wrap">
        <StatusPill state={state} />
        {/* The "checked X ago" stamp only makes sense when the pill is
            showing the cached state we got from Mongo. If we downgraded
            to "Not checked yet" (no live key or the operator changed
            the model since the check) the stamp would point at a check
            that's no longer relevant; suppress it. */}
        {target.status?.lastCheckedAt
          && state !== 'checking'
          && state === cachedState && (
            <span className="text-xs text-muted-foreground">
              checked {relativeTime(target.status.lastCheckedAt)}
            </span>
          )}
        {modelChangedSinceCheck && !noLiveKey && (
          <span className="text-xs text-muted-foreground">
            Model selection changed since last check — re-verify.
          </span>
        )}
        {noLiveKey && (
          <span className="text-xs text-muted-foreground">
            API key not set — re-verify after setting it.
          </span>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="ml-auto"
          onClick={onCheck}
          disabled={checking}
        >
          {checking ? 'Checking…' : 'Check now'}
        </Button>
      </div>
      {state === 'notWorking' && target.status?.fixMessage && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 space-y-1.5">
          <p className="text-sm font-medium text-destructive">
            {target.status.fixMessage}
          </p>
          {target.status.errorMessage && (
            <p className="text-xs text-muted-foreground">
              Provider said: <span className="font-mono">{target.status.errorMessage}</span>
            </p>
          )}
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

/** The masked API-key line for a row — "API key: sk-a…Z9k2 (OPENAI_API_KEY)".
 * Lets an operator confirm WHICH key is configured (e.g. after a
 * rotation) without exposing the secret; the preview is masked server-
 * side and the full value never reaches the browser. Shows "not set"
 * when no key in the chain is configured. */
function ApiKeyPreview({ target }: { target: ModelTargetRow | undefined }) {
  if (!target) return null
  return (
    <p className="text-xs text-muted-foreground">
      <span className="font-medium text-foreground/70">API key:</span>{' '}
      {target.apiKeyPreview ? (
        <>
          <span className="font-mono">{target.apiKeyPreview}</span>
          {/* Name the env var the preview was ACTUALLY read from — not a
              fixed chain[0], which would misattribute a fallback var
              during a rotation. */}
          {target.apiKeyEnvUsed && (
            <span className="text-muted-foreground/70"> ({target.apiKeyEnvUsed})</span>
          )}
        </>
      ) : target.servedLocally ? (
        // The local bundled model runs on the machine and needs no key, so
        // show that rather than "not set", which reads as a misconfiguration.
        <span className="italic">not needed (served locally)</span>
      ) : (
        <span className="italic">not set</span>
      )}
    </p>
  )
}

/** One line of an env-var fallback chain — "API key: FOO → BAR".
 * Rendered on the read-only embedding row, where there are no edit
 * controls and the chain is the only way to see which environment
 * variables back the model. */
function EnvChainLine({ label, chain }: { label: string; chain: readonly string[] }) {
  return (
    <div>
      <span className="font-medium text-foreground/70">{label}:</span>{' '}
      {chain.length > 0
        ? chain.map((n, i) => (
            <span key={n} className="font-mono">
              {n}
              {i < chain.length - 1 ? ' → ' : ''}
            </span>
          ))
        : '(none)'}
    </div>
  )
}

/** Map full-chain model source → the legacy Provenance prop's source
 * union. The Provenance component understands `'doc' | 'env' | 'builtIn'
 * | 'unset'`; we collapse the resolver's six tiers down to those four.
 * `per-run` doesn't appear in the GET payload (per-run only exists at
 * runtime call sites). */
function modelChainSourceToProvenance(
  source: ResolvedAgentEntry['model']['source'],
): 'doc' | 'env' | 'builtIn' | 'unset' {
  switch (source) {
    case 'app-settings': return 'doc'
    case 'per-agent-env':
    case 'default-env':
    case 'legacy-env':
      return 'env'
    case 'hardcoded': return 'builtIn'
    case 'per-run': return 'doc' // not expected in GET, but safe-collapse
    default: return 'unset'
  }
}

function temperatureChainSourceToProvenance(
  source: ResolvedAgentEntry['temperature']['source'],
): 'doc' | 'env' | 'builtIn' | 'unset' {
  switch (source) {
    case 'app-settings': return 'doc'
    case 'per-agent-env':
    case 'default-env':
      return 'env'
    case 'per-run': return 'doc'
    case 'unset':
    default:
      return 'unset'
  }
}

interface RowDraft {
  /** `<provider>:<model>` namePattern, or `''` for "cleared / cascade". */
  namePattern: string
  /** Raw text in the temperature input. String so empty (`''`) is
   * distinguishable from `0` and partial input (`0.`) doesn't NaN-coerce
   * mid-keystroke. */
  temperature: string
  /** True when the operator has edited EITHER field. Until set, the
   * row is treated as pre-filled-but-untouched and excluded from the
   * PUT body. */
  dirty: boolean
}

export function AgentModelsPage() {
  const [data, setData] = useState<AppSettingsResponse | null>(null)
  const [pricingRows, setPricingRows] = useState<ModelPricingRow[] | null>(null)
  // #2014 — retired models are absent from `pricingRows`, but an agent may
  // still be assigned one; the picker needs them to explain the current value.
  const [retiredModels, setRetiredModels] = useState<readonly RetiredModelSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Verification health — loaded alongside config but kept on its own
  // state so a verification fetch failure NEVER blocks the assignment
  // form. Health is supporting context; configuring a model must work
  // even when the verification service is down.
  const [targets, setTargets] = useState<ModelTargetRow[] | null>(null)
  const [perRowChecking, setPerRowChecking] = useState<Record<string, boolean>>({})
  const [perRowError, setPerRowError] = useState<Record<string, string>>({})
  const [checkAllRunning, setCheckAllRunning] = useState(false)
  const [checkAllMessage, setCheckAllMessage] = useState<string | null>(null)

  async function reload(): Promise<void> {
    setLoading(true)
    try {
      const [settings, pricing] = await Promise.all([
        adminAppSettingsAPI.get(),
        adminPricingAPI.list(),
      ])
      setData(settings)
      setPricingRows(pricing.rows)
      setRetiredModels(pricing.retired)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load agent models')
    } finally {
      setLoading(false)
    }
  }

  async function reloadVerification(): Promise<void> {
    try {
      const result = await adminModelVerificationAPI.list()
      setTargets(result.targets)
    } catch {
      // Soft signal — leave the health blocks hidden rather than throwing
      // an error toast over the config form.
    }
  }

  useEffect(() => {
    void reload()
    void reloadVerification()
  }, [])

  async function checkRow(targetId: string): Promise<void> {
    setPerRowChecking((s) => ({ ...s, [targetId]: true }))
    setPerRowError((s) => ({ ...s, [targetId]: '' }))
    try {
      const result = await adminModelVerificationAPI.verify(targetId)
      // Splice just the freshly-checked target back in — keeps the
      // in-flight indicator honest for the other rows.
      setTargets((rows) =>
        rows ? rows.map((row) => (row.targetId === targetId ? result.target : row)) : rows,
      )
    } catch (e) {
      setPerRowError((s) => ({
        ...s,
        [targetId]: e instanceof Error ? e.message : 'Check failed',
      }))
    } finally {
      setPerRowChecking((s) => ({ ...s, [targetId]: false }))
    }
  }

  async function checkAll(): Promise<void> {
    setCheckAllRunning(true)
    setCheckAllMessage(null)
    try {
      await adminModelVerificationAPI.runAllNow()
      setCheckAllMessage('Check started. Statuses below will refresh shortly.')
      // The scheduled-job run executes asynchronously; poll a few times
      // so the page catches up to the new statuses without a manual
      // reload.
      for (let i = 0; i < 6; i++) {
        await new Promise((resolve) => setTimeout(resolve, 1500))
        await reloadVerification()
      }
    } catch (e) {
      setCheckAllMessage(
        e instanceof Error
          ? `Couldn't start the check: ${e.message}`
          : "Couldn't start the check",
      )
    } finally {
      setCheckAllRunning(false)
    }
  }

  if (loading && !data) {
    return (
      <div className="space-y-6 max-w-3xl">
        <AgentsSubNav />
        <p className="text-sm text-muted-foreground">Loading agent models…</p>
      </div>
    )
  }
  if (error && !data) {
    return (
      <div className="space-y-6 max-w-3xl">
        <AgentsSubNav />
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      </div>
    )
  }
  if (!data || !pricingRows) return null

  const statusByTarget = new Map((targets ?? []).map((t) => [t.targetId, t]))
  const embeddingTarget = targets?.find((t) => t.targetKind === 'embedding')
  // Fire the red banner only when a row will ACTUALLY render its fix note — the
  // same effective state (including the row's in-flight `checking`) plus the
  // note's own `fixMessage` guard. Keying off raw `status.state` used to light
  // the banner for downgraded rows that show no note (#2090).
  const anyNotWorking = (targets ?? []).some((t) =>
    isNotWorkingWithFix(t, perRowChecking[t.targetId] ?? false),
  )

  return (
    <div className="space-y-6 max-w-3xl">
      <AgentsSubNav />
      <div>
        <h1 className="text-2xl font-semibold">Per-agent model assignment</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pick each agent's model + temperature, and see whether that model is currently working. Fields shown{' '}
          <em className="text-muted-foreground/80">in muted style</em> are inherited from the env cascade —{' '}
          changing them creates an explicit doc override.
        </p>
      </div>

      <AdvancedSettingsNotice />

      {/* Health banner — "Check all now" kicks the 20-minute scheduled
          sweep early (same path as the next auto-tick). The "Change
          schedule" link points at the scheduled-jobs page for this
          check. Only rendered once the verification list has loaded. */}
      {targets && (
        <Card>
          <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              {anyNotWorking ? (
                <span className="text-destructive font-medium">
                  One or more models are not working. See the fix note under each row.
                </span>
              ) : (
                <span className="text-muted-foreground">
                  Models are checked automatically on a schedule. Use “Check now” after a key change.
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => void checkAll()}
                disabled={checkAllRunning}
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${checkAllRunning ? 'animate-spin' : ''}`} />
                {checkAllRunning ? 'Checking all…' : 'Check all now'}
              </Button>
              <Link
                to="/admin/scheduled-jobs/verifyAllModels"
                className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                title="Open the scheduled-jobs page for this check"
              >
                Change schedule
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
      {checkAllMessage && (
        <p className="text-sm text-muted-foreground" role="status">
          {checkAllMessage}
        </p>
      )}

      <AgentModelsSection
        doc={data.agentModels}
        resolved={data.resolved}
        capabilityRequirements={data.agentCapabilityRequirements}
        originPolicyActive={data.originPolicyActive === true}
        pricingRows={pricingRows}
                    retiredModels={retiredModels}
        statusByTarget={statusByTarget}
        perRowChecking={perRowChecking}
        perRowError={perRowError}
        onCheckRow={checkRow}
        onSaved={reload}
      />

      {embeddingTarget && (
        <EmbeddingModelCard
          target={embeddingTarget}
          checking={perRowChecking[embeddingTarget.targetId] ?? false}
          error={perRowError[embeddingTarget.targetId]}
          onCheck={() => void checkRow(embeddingTarget.targetId)}
        />
      )}
    </div>
  )
}

/** Read-only health row for the embedding model. The embedding model
 * isn't an agent, so it has no assignment controls — but an operator
 * watching for a stale embedding key wants to see its health from the
 * same screen. Shows the resolved model + env-var chains + status, with
 * the same per-row "Check now" affordance as the agent rows. */
function EmbeddingModelCard({
  target,
  checking,
  error,
  onCheck,
}: {
  target: ModelTargetRow
  checking: boolean
  error: string | undefined
  onCheck: () => void
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{target.displayName}</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          <span className="font-mono">{target.provider}</span>
          {' · '}
          <span className="font-mono">{target.model}</span>
          {target.expectedDimensions !== undefined && (
            <>
              {' · '}
              <span>{target.expectedDimensions} dimensions</span>
            </>
          )}
          {' · read-only (set by environment, not editable here)'}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <ModelHealth target={target} checking={checking} error={error} onCheck={onCheck} />
        <ApiKeyPreview target={target} />
        <div className="text-xs text-muted-foreground space-y-1">
          <EnvChainLine label="API key env" chain={target.apiKeyEnvChain} />
          <EnvChainLine label="Base URL" chain={target.baseUrlEnvChain} />
          <EnvChainLine label="Model" chain={target.modelEnvChain} />
        </div>
      </CardContent>
    </Card>
  )
}

/** The plain-language "why this pick" tooltip from a ranked model's numbers.
 * The ranking library returns numbers only; formatting lives here so the
 * library stays presentation-neutral (#1844 Slice 3). Leads with the honest
 * anchor (the model's real average) so a percent-looking scaled score never
 * misreads as an absolute rating. */
function explainPick(m: RankedModel): string {
  const e = m.explanation
  const cost = m.costCategory
    ? `${m.costCategory[0].toUpperCase()}${m.costCategory.slice(1)} cost`
    : 'unpriced'
  const combined = Math.round(e.combinedScore)
  if (e.hasQualityData && e.meanQuality !== null) {
    return `Averaged ${Math.round(e.meanQuality)}/100 over ${e.runCount} run${e.runCount === 1 ? '' : 's'} · ${cost}. Combined ${combined}/100 at this setting (relative to this shortlist).`
  }
  return `No quality history yet · ${cost}. Ranked on cost at this setting (combined ${combined}/100).`
}

/**
 * The "Help me choose" advisory slider for a SCORED agent (#1844 Slice 3).
 * Ranks the de-duplicated union of the curated shortlist and the models this
 * agent has actually run, at the current cost↔performance setting, and offers a
 * one-click Assign that only fills the picker (the operator still Saves — the
 * slider never writes config). `leaderboard === undefined` means still loading.
 */
function HelpMeChoose({
  agent,
  pricingRows,
  leaderboard,
  weight,
  onWeight,
  onAssign,
}: {
  agent: AdminAgentName
  pricingRows: readonly ModelPricingRow[]
  leaderboard: readonly ModelLeaderboardRow[] | undefined
  weight: number
  onWeight: (w: number) => void
  onAssign: (namePattern: string) => void
}) {
  const ranked = useMemo(
    () =>
      leaderboard === undefined
        ? []
        : rankModels(buildRecoInputs(pricingRows, leaderboard), weight),
    [pricingRows, leaderboard, weight],
  )
  const pick = ranked[0]
  return (
    <div className="border-t border-dashed pt-3 mt-1 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium">Help me choose</span>
        <span className="text-xs text-muted-foreground">
          advisory — highlights a pick, never changes the model on its own
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={weight}
        onChange={(e) => onWeight(Number(e.target.value))}
        disabled={leaderboard === undefined}
        aria-label={`${agent} cost versus performance`}
        className="w-full accent-primary"
      />
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>◀ Cost-efficient</span>
        <span>Balanced</span>
        <span>Highest performing ▶</span>
      </div>
      {leaderboard === undefined ? (
        <p className="text-xs text-muted-foreground">Finding a recommendation…</p>
      ) : pick ? (
        <div
          className="flex items-center gap-2 flex-wrap rounded-md border bg-muted/30 px-3 py-2"
          title={explainPick(pick)}
        >
          <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">Suggested</span>
          <span className="font-mono text-sm font-semibold">{pick.namePattern}</span>
          <CostCategoryPill category={pick.costCategory} />
          {!pick.explanation.hasQualityData && (
            <span className="text-[10px] text-muted-foreground">no quality data yet</span>
          )}
          <Button type="button" size="sm" className="ml-auto" onClick={() => onAssign(pick.namePattern)}>
            Assign
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No priced models to rank yet.</p>
      )}
    </div>
  )
}

/** The cost-only note for an agent OttoTester doesn't score yet (#1844 Slice 3).
 * A greyed, non-interactive slider keeps visual parity with the scored rows;
 * the note explains why — never an invented quality score. */
function HelpMeChooseCostOnly() {
  return (
    <div className="border-t border-dashed pt-3 mt-1 space-y-2">
      <div className="text-sm font-medium">Help me choose</div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={0.2}
        readOnly
        disabled
        aria-hidden="true"
        tabIndex={-1}
        className="w-full opacity-40"
      />
      <p className="text-xs text-muted-foreground rounded-md border border-dashed bg-muted/20 px-3 py-2">
        Ranks by <strong className="font-medium text-foreground">cost only</strong> — OttoTester doesn't score
        this agent's output yet, so there's no quality signal to balance against. Pick a model from the list by
        cost.{' '}
        <span className="text-muted-foreground/80">
          (Generator, planner, and healer have a quality signal; the rest light up automatically once they get
          one.)
        </span>
      </p>
    </div>
  )
}

/**
 * The always-visible summary line for one agent (#2112).
 *
 * Seven agents each ran about 570px tall, separated by a 1px line and 16px of
 * space — the same weight as the three bordered boxes INSIDE each one, on the
 * same white ground, with the agent's name set in the very `Label` style used
 * by the fields beneath it. Nothing told the eye which line was the boundary,
 * so it was easy to edit the wrong agent's model. This header is that missing
 * boundary: a filled strip, the name at heading size, and — since the body
 * below it is now hidden until asked for — enough of a summary to answer
 * "what is this agent set to?" without opening anything.
 *
 * Every value here is read through the SAME helper the open body uses:
 * `effectiveModelState` + `StatusPill` for health, `modelCostCategory` +
 * `CostCategoryPill` for cost, the draft's own `dirty` flag for unsaved edits.
 * A summary that re-derived any of those could disagree with the row it
 * summarises, which is worse than not summarising at all.
 *
 * Absent target renders no status, exactly as `ModelHealth` does: the
 * verification list may still be loading, or its fetch may have soft-failed
 * (see `reloadVerification`), and neither is "not checked yet". Inventing a
 * header-only "status unavailable" would make this the one component with a
 * fifth health state.
 */
function AgentRowHeader({
  agent,
  open,
  bodyId,
  modelSpec,
  temperature,
  costCategory,
  dirty,
  target,
  checking,
  onToggle,
}: {
  agent: AdminAgentName
  open: boolean
  bodyId: string
  modelSpec: string
  /** The draft temperature, verbatim. Empty means the field was cleared, which
   * the open row words as "(provider default)" — matched here rather than
   * abbreviated, so the two never read as different settings. */
  temperature: string
  costCategory: ReturnType<typeof modelCostCategory>
  dirty: boolean
  target: ModelTargetRow | undefined
  checking: boolean
  onToggle: () => void
}) {
  return (
    <button
      // type="button" is load-bearing: this sits inside the section's <form>,
      // and a default-type button submits it — so opening a row would have
      // saved the page.
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      // Only while the body is actually mounted: `aria-controls` pointing at an
      // id that is not in the document is not conformant, and the body is
      // unmounted when the row is closed. `aria-expanded` carries the state on
      // its own either way.
      aria-controls={open ? bodyId : undefined}
      className={`w-full flex items-center gap-x-3 gap-y-1.5 flex-wrap px-3 py-2.5 text-left transition-colors
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ring-inset
        ${open ? 'bg-muted' : 'bg-muted/60 hover:bg-muted'}`}
    >
      <ChevronRight
        aria-hidden="true"
        className={`h-4 w-4 flex-none text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
      />
      <span className="text-base font-semibold">{AGENT_LABEL[agent]}</span>
      {/* The bare spaces between these children are not decoration. A button's
          accessible name is its text content concatenated, and flex drops
          whitespace-only children from the layout — so without them a screen
          reader reads "plannergpt-5.4-miniTemperature", while the screen looks
          identical either way.

          min-w-0 is what lets the model spec truncate instead of pushing the
          badges off the edge — the content column is max-w-3xl (768px), which
          a long provider:model spec plus every badge will exceed. */}
      {' '}
      <span className="ml-auto flex min-w-0 items-center gap-2 flex-wrap justify-end text-xs text-muted-foreground">
        {dirty && (
          <Badge variant="warning" className="text-[10px]">Unsaved</Badge>
        )}
        {' '}
        <span className="font-mono text-xs text-foreground truncate max-w-full">{modelSpec}</span>
        {' '}
        {costCategory && <CostCategoryPill category={costCategory} />}
        {' '}
        <span className="whitespace-nowrap">
          Temperature {temperature.trim() ? temperature.trim() : '(provider default)'}
        </span>
        {' '}
        {target && <StatusPill state={effectiveModelState(target, checking)} />}
      </span>
    </button>
  )
}

/**
 * A row for an agent this backend has not returned a model for.
 *
 * Shown disabled rather than dropped. The condition is a deployment window —
 * the browser holds a newer bundle than the server it is talking to, so an
 * agent the bundle knows about (the refiner, #2132) is missing from the
 * response. Hiding the row would read as the agent having been deleted;
 * showing it greyed out says what is actually true, which is that it is not
 * ready yet. Not a `<button>`: there is nothing to open, and a disabled
 * control the operator can tab to but not use is worse than plain text.
 */
function UnavailableAgentRow({ agent }: { agent: AdminAgentName }) {
  return (
    <div className="w-full flex items-center gap-x-3 gap-y-1.5 flex-wrap px-3 py-2.5 text-left bg-muted/30">
      {/* Holds the chevron's width so the agent names stay in one column with
          the rows above and below. */}
      <span aria-hidden="true" className="h-4 w-4 flex-none" />
      <span className="text-base font-semibold text-muted-foreground">{AGENT_LABEL[agent]}</span>
      {' '}
      <span className="ml-auto text-xs text-muted-foreground">
        Not available yet. It will appear once the update finishes.
      </span>
    </div>
  )
}

function AgentModelsSection({
  doc,
  resolved,
  capabilityRequirements,
  originPolicyActive,
  pricingRows,
  retiredModels,
  statusByTarget,
  perRowChecking,
  perRowError,
  onCheckRow,
  onSaved,
}: {
  doc: AgentModelDefaults | undefined
  resolved: ResolvedAppSettings
  capabilityRequirements: Record<string, { required: readonly string[] }> | undefined
  /** #2107 — this deployment keeps excluded models out of the catalog. A model
   * that is not in the catalog cannot be used, so the picker stops offering
   * one it would normally keep visible, and a row still pointing at one
   * refuses to save. */
  originPolicyActive: boolean
  pricingRows: readonly ModelPricingRow[]
  /** Models the catalog no longer offers (#2014). Not in `pricingRows` — an
   * operator cannot pick one — but an agent may already be assigned one, and
   * the picker has to say which of the two things happened to it. */
  retiredModels: readonly RetiredModelSummary[]
  statusByTarget: ReadonlyMap<string, ModelTargetRow>
  perRowChecking: Record<string, boolean>
  perRowError: Record<string, string>
  onCheckRow: (targetId: string) => void
  onSaved: () => Promise<void>
}) {
  /** The agents the server actually sent a resolved entry for.
   *
   * Normally that is every name in `AGENTS`. It is NOT during a deployment: the
   * browser can hold a newer bundle than the backend it is talking to, so an
   * agent added on the frontend (the refiner, #2132) is in `AGENTS` while the
   * server has never heard of it. `resolved.agentModels[a]` is then undefined,
   * and reading `r.model.spec` off it throws and blanks the entire page rather
   * than the one row that cannot be drawn.
   *
   * This is the list of rows that can be EDITED — what the draft is seeded
   * from and what a save walks. The render still walks all of `AGENTS`, drawing
   * a disabled row for the rest, so an agent that is merely not ready yet never
   * reads as one that was deleted. */
  const presentAgents = AGENTS.filter((a) => resolved.agentModels[a] !== undefined)

  /** One row's starting values — pre-fill from the doc tier when set, else
   * from the resolved-chain value. `dirty` starts false so a save without
   * edits sends `agentModels: {}` (no overrides written). Shared by the mount
   * seed and the late-hydration effect below so a row that appears mid-session
   * is filled exactly like one that was there from the start. */
  function seedRow(a: AdminAgentName, r: ResolvedAgentEntry): RowDraft {
    const docEntry = doc?.[a]
    // The resolved entry is a PARAMETER rather than looked up here, so a caller
    // cannot reach this function without one. That is what makes the partial
    // wire type load-bearing instead of decorative (#2137).
    // Pre-fill the namePattern dropdown: doc value first, else the
    // resolved-chain model spec (which is always populated — the chain
    // bottoms at the hardcoded floor).
    const docNamePattern = overrideToNamePattern(docEntry)
    // Pre-fill temperature: doc value first, else the resolved-chain
    // value. When neither is set we leave the input empty so the
    // placeholder ("(provider default)") shows.
    const initialTemp = docEntry?.temperature !== undefined
      ? String(docEntry.temperature)
      : r.temperature.value !== null
        ? String(r.temperature.value)
        : ''
    return {
      namePattern: docNamePattern || r.model.spec,
      temperature: initialTemp,
      dirty: false,
    }
  }

  const [draft, setDraft] = useState<Record<AdminAgentName, RowDraft>>(() => {
    const out = {} as Record<AdminAgentName, RowDraft>
    for (const a of presentAgents) {
      const r = resolved.agentModels[a]
      if (r) out[a] = seedRow(a, r)
    }
    return out
  })

  /** Fill in rows for agents that show up after mount.
   *
   * `presentAgents` is recomputed every render, so when a deployment finishes
   * and the backend starts returning an agent it did not have a moment ago,
   * that agent joins the render list. `draft` is seeded ONCE, so it has no
   * entry for it, and the render path would dereference undefined — the same
   * blank page one step later. Additive only: an agent already in `draft` is
   * left untouched, so a refetch can never clobber an edit in progress.
   *
   * The effect lands after the commit that first includes the new agent, so
   * that one frame renders with no row yet; the render skips it rather than
   * throwing. (Copilot review on #2137.) */
  useEffect(() => {
    const unseeded = presentAgents.filter((a) => draft[a] === undefined)
    if (unseeded.length === 0) return
    setDraft((d) => {
      const next = { ...d }
      for (const a of unseeded) {
        const r = resolved.agentModels[a]
        if (r && next[a] === undefined) next[a] = seedRow(a, r)
      }
      return next
    })
  })
  const [rowErrors, setRowErrors] = useState<Partial<Record<AdminAgentName, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)

  /** Which agents are expanded (#2112). Everything starts closed; the seven
   * headers then fit together, which is the whole point. Nothing here is
   * persisted — reopening the page reopens nothing. */
  const [openAgents, setOpenAgents] = useState<ReadonlySet<AdminAgentName>>(() => new Set())

  function toggleAgent(agent: AdminAgentName): void {
    setOpenAgents((prev) => {
      const next = new Set(prev)
      if (!next.delete(agent)) next.add(agent)
      return next
    })
  }

  function openAgentRows(agents: readonly AdminAgentName[]): void {
    setOpenAgents((prev) => {
      // Returning the SAME set when nothing changes is what lets React bail
      // out of the re-render. The auto-open effect below runs after every
      // render, so an updater that always minted a fresh Set — even one with
      // identical contents — would re-render, re-run the effect, and spin
      // forever.
      if (agents.every((a) => prev.has(a))) return prev
      const next = new Set(prev)
      for (const a of agents) next.add(a)
      return next
    })
  }

  /** A failing agent opens itself, so the page banner's promise — "See the fix
   * note under each row" — is still true with the rows closed. The note lives
   * inside the body, so a closed row would make the banner point at nothing.
   *
   * Fires on ENTRY into the failing set, not as a standing assertion. Verified
   * against how failures actually arrive: `checkAll` reloads verification six
   * times at 1500ms, so an effect that simply re-opened every failing agent on
   * each pass would fight a user who had closed one, reopening it every 1.5
   * seconds for nine seconds. Tracking the previous set means a hand-closed row
   * stays closed, and only a model that newly starts failing opens itself.
   *
   * A null `previous` is the first pass, where everything already failing is
   * "new" — that is the page-load case. */
  const previouslyFailing = useRef<ReadonlySet<AdminAgentName> | null>(null)
  useEffect(() => {
    const failing = new Set<AdminAgentName>()
    for (const a of presentAgents) {
      const targetId = agentTargetId(a)
      const t = statusByTarget.get(targetId)
      if (t && isNotWorkingWithFix(t, perRowChecking[targetId] ?? false)) failing.add(a)
    }
    const previous = previouslyFailing.current
    previouslyFailing.current = failing
    openAgentRows(presentAgents.filter((a) => failing.has(a) && !previous?.has(a)))
  })

  // Per-agent quality leaderboards for the "Help me choose" slider (#1844
  // Slice 3), fetched non-blocking like the verification list — a slow or
  // failed BI aggregation must never block the assignment form. `undefined` =
  // still loading (slider shows "Finding a recommendation…"); `[]` = loaded or
  // soft-failed with no history, so the slider degrades to a cost-only ranking
  // rather than spinning forever.
  const [leaderboards, setLeaderboards] = useState<
    Partial<Record<ModelComparisonAgent, ModelLeaderboardRow[]>>
  >({})
  useEffect(() => {
    let cancelled = false
    for (const a of SCORED_AGENTS) {
      biAPI
        .modelComparison(undefined, a)
        .then((payload) => {
          if (!cancelled) setLeaderboards((s) => ({ ...s, [a]: payload.leaderboard }))
        })
        .catch(() => {
          if (!cancelled) setLeaderboards((s) => ({ ...s, [a]: [] }))
        })
    }
    return () => {
      cancelled = true
    }
  }, [])

  // Slider weight per agent (0 = cost-efficient, 1 = highest performing);
  // defaults to Balanced. Ephemeral — resets on reload, not persisted.
  const [sliderWeights, setSliderWeights] = useState<Partial<Record<AdminAgentName, number>>>({})

  /** All available `<provider>:<model>` patterns from the pricing
   * catalog, sorted for stable display. Memoized so a re-render
   * doesn't reshuffle the dropdown. */
  const catalogPatterns = useMemo(
    () => [...pricingRows.map((r) => r.namePattern)].sort((a, b) => a.localeCompare(b)),
    [pricingRows],
  )

  /** Pricing rows keyed by namePattern — one lookup table so building each
   * option's facets is O(1) per option, not a `.find` over ~600 rows per
   * option (#1844 Slice 1.5). */
  const rowByPattern = useMemo(
    () => new Map(pricingRows.map((r) => [r.namePattern, r])),
    [pricingRows],
  )

  /** Provider keys present in the catalog, in alphabetical order — the
   * SearchableSelect renders header bands in this order. Patterns
   * without a colon ("bare model" rows in the catalog) fall through to
   * an `'other'` bucket so they're still visible. */
  const providerGroups = useMemo(() => {
    const seen = new Set<string>()
    for (const p of catalogPatterns) {
      const { provider } = parseNamePattern(p)
      seen.add(provider ?? 'other')
    }
    return [...seen].sort((a, b) => a.localeCompare(b)).map((key) => ({
      key,
      label: key,
    }))
  }, [catalogPatterns])

  /** Per-agent options list. If the agent's resolved model (or doc
   * value) isn't already in the catalog, we surface it as a synthetic
   * extra option at the top labeled `(current: not in the model catalog)`
   * so the operator isn't forced to switch models just because the
   * pricing catalog hasn't seen this spec yet. The orphan option has
   * no `group` so the SearchableSelect renders it ungrouped (above
   * the provider headers). */
  /** Capabilities the given agent requires, from the map the SERVER sends on
   * the app-settings response (#1928). Deliberately not a constant in this
   * file: this rule decides whether a run may spend money, and hand-copying a
   * correctness rule into a second place is the drift #1902 spent two slices
   * removing from the agent prompts.
   *
   * Empty when the server has not sent it (older backend, or still loading),
   * which makes the agent facet simply not apply rather than filtering
   * everything out. */
  function requiredCapsFor(a: AdminAgentName): readonly string[] {
    return capabilityRequirements?.[a]?.required ?? []
  }

  /** Is this model known to fall short of what the agent needs? Only a
   * positively-reported `false` counts — a catalog that is silent must never
   * warn, or every model missing from both upstream sources would nag. */
  function modelFallsShort(a: AdminAgentName, namePattern: string): string | null {
    const required = requiredCapsFor(a)
    if (required.length === 0) return null
    const row = rowByPattern.get(namePattern)
    if (!row) return null
    if (required.includes('toolCalling') && row.toolCalling === false) return 'toolCalling'
    return null
  }

  /** Retired models by spec, for the picker's current-value label (#2014). */
  const retiredByPattern_ = useMemo(() => retiredByPattern(retiredModels), [retiredModels])

  /** Is this row pointing at something the catalog no longer offers? (#2107)
   *
   * The catalog is what decides selectability, so off-catalog means unusable
   * when the policy is on — including a model that is merely unknown rather
   * than excluded. The frontend cannot tell those apart once the row is
   * deleted: there is nothing left to read a country off. */
  function isOffCatalog(a: AdminAgentName): boolean {
    const current = draft[a].namePattern
    return current !== '' && !catalogPatterns.includes(current)
  }

  function optionsForAgent(a: AdminAgentName): readonly SearchableSelectOption[] {
    const current = draft[a].namePattern
    // #2107 — no orphan fallback under the policy. Normally this option exists
    // so nobody is forced to switch models just because the catalog has not
    // caught up; here it would keep an agent on a model the deployment is not
    // supposed to have.
    const orphan = !originPolicyActive && isOffCatalog(a)
    const orphanOpt: SearchableSelectOption | null = orphan
      // #2014 — say WHICH kind of missing. A retired model is one an operator
      // could pick yesterday; a never-seen spec is a different situation and
      // keeps the original wording.
      ? { value: current, label: `${current} (current: ${retiredOptionSuffix(retiredByPattern_.get(current))})` }
      : null
    const catalogOpts: SearchableSelectOption[] = catalogPatterns.map((p) => {
      const { provider } = parseNamePattern(p)
      const row = rowByPattern.get(p)
      // #1844 Slice 1.5 — facet membership per option (curated/frontier from
      // the row's collections overlay, reads-images from imageInput). The
      // orphan option below stays facet-exempt (no `facets`) so a current
      // model missing from the catalog is never hidden behind a facet.
      // #1844 Slice 2 — the relative cost pill rides along as the option's
      // trailing adornment, so the operator compares cost WHILE browsing the
      // list rather than having to pick a model first to see its cost. An
      // unpriced row carries no category → no pill.
      const cat = row?.costCategory
      return {
        value: p,
        label: p,
        group: provider ?? 'other',
        facets: optionFacets(row, requiredCapsFor(a)),
        // The row lays these out on their own line and handles wrapping, so
        // this is just the badge set — no alignment or spacing of its own.
        trailing: (
          <span className="flex items-center gap-1 flex-wrap">
            <CapabilityBadge value={row?.toolCalling} label="🔧 tool calling" />
            {reasoningBadge(row)}
            {formatContextWindow(row?.maxInputTokens) && (
              <Badge variant="secondary" className="w-fit text-[10px]">
                {formatContextWindow(row?.maxInputTokens)}
              </Badge>
            )}
            {cat ? <CostCategoryPill category={cat} /> : null}
          </span>
        ),
      }
    })
    return orphanOpt ? [orphanOpt, ...catalogOpts] : catalogOpts
  }

  /** Whether the current dropdown value matches what would have been
   * shown by the resolver chain pre-fill (i.e. operator hasn't pinned
   * anything yet). Used to apply the muted "inherited" styling. */
  function isInheritedNamePattern(a: AdminAgentName): boolean {
    if (draft[a].dirty) return false
    return !doc?.[a]?.model
  }

  function isInheritedTemperature(a: AdminAgentName): boolean {
    if (draft[a].dirty) return false
    return doc?.[a]?.temperature === undefined
  }

  /** Parse a temperature draft into a number, `null` (empty/cleared),
   * or a string error. Mirrors the server-side rule: undefined OR
   * finite number in [0, 2]. */
  function parseTempDraft(raw: string): number | null | string {
    const trimmed = raw.trim()
    if (!trimmed) return null
    const n = Number(trimmed)
    if (!Number.isFinite(n)) return 'must be a finite number in [0, 2]'
    if (n < 0 || n > 2) return 'must be in [0, 2]'
    return n
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    setRowErrors({})
    try {
      const agentModels: AgentModelDefaults = {}
      const newRowErrors: Partial<Record<AdminAgentName, string>> = {}

      for (const a of presentAgents) {
        const row = draft[a]
        // Defense: only persist when the operator actually edited the
        // row. A pre-filled inherited value must NOT auto-persist on
        // save — the whole point of the inherited styling is that the
        // operator can "leave it as is" without baking the env value
        // into the doc tier.
        if (!row.dirty) continue

        // #2107 — hiding the option is not enough. The draft still holds the
        // old spec, so editing only the temperature would mark the row dirty
        // and write the removed model straight back. Refuse the save instead:
        // it says "cannot be used" out loud rather than changing the
        // configuration behind the user.
        if (originPolicyActive && isOffCatalog(a)) {
          newRowErrors[a] = 'this model is no longer available — pick one from the list before saving'
          continue
        }

        const override: AgentModelOverride = {}
        const np = row.namePattern.trim()
        if (np) {
          const { provider, model } = parseNamePattern(np)
          if (provider) override.provider = provider
          override.model = model
        }
        // Temperature parsing + client-side validation. Empty input
        // means clear-the-override.
        const tempParsed = parseTempDraft(row.temperature)
        if (typeof tempParsed === 'string') {
          newRowErrors[a] = `temperature ${tempParsed}`
          continue
        }
        if (tempParsed !== null) override.temperature = tempParsed

        if (override.provider !== undefined || override.model !== undefined || override.temperature !== undefined) {
          agentModels[a] = override
        }
        // If the row is dirty but every field cleared to empty, we
        // intentionally OMIT the key from agentModels — the partial-PUT
        // contract treats a missing agent key as "leave existing
        // override alone". To explicitly clear we'd need a different
        // affordance; this matches the original UX where empty inputs
        // were a no-op on save.
      }

      if (Object.keys(newRowErrors).length > 0) {
        setRowErrors(newRowErrors)
        // The message renders inside the body, so a rejected save on a closed
        // row would report nothing at all (#2112).
        openAgentRows(Object.keys(newRowErrors) as AdminAgentName[])
        return
      }

      // #1928 — saving a model the catalog reports as unable to meet the
      // agent's requirement RECORDS the operator's assertion, rather than just
      // warning and moving on. Without this the runtime gate (#1927) would
      // refuse the very model they just insisted on, so the warning would be a
      // rubber stamp and the operator's decision would die at the UI layer.
      //
      // Written per model row, not per agent: capability is a property of the
      // model, so the correction must apply everywhere that model is used.
      // Deduped because two agents can be saved onto the same model in one go.
      const overrides = new Map<string, string>()
      for (const a of presentAgents) {
        if (!draft[a].dirty) continue
        // The DRAFT's namePattern, not the assembled `agentModels[a].model`:
        // the PUT body splits `provider:model` into two fields, so its `model`
        // is the bare name while the catalog is keyed on the full spec. Reading
        // the assembled body found nothing and silently skipped every override.
        const spec = draft[a].namePattern.trim()
        if (!spec || !modelFallsShort(a, spec)) continue
        const row = rowByPattern.get(spec)
        if (row?._id) overrides.set(row._id, spec)
      }
      for (const rowId of overrides.keys()) {
        // Sequential, and deliberately not swallowed: if recording the override
        // fails, the operator must know their model was saved WITHOUT it —
        // silently proceeding would leave the run to fail at the gate with no
        // hint why.
        await adminPricingAPI.setCapabilityOverride(rowId, 'toolCalling', true)
      }

      await adminAppSettingsAPI.update({ agentModels })
      // The write succeeded, so every drafted row now matches what is stored
      // and nothing is unsaved any more. Resetting the flags here is the only
      // thing that can clear them: `draft` is seeded by a lazy `useState`
      // initializer that runs ONCE per mount, and `reload()` leaves `data` set
      // while it refetches (the page gates on `loading && !data`), so this
      // section is never unmounted and the initializer never runs again.
      // Without this, a flag set by a row editor lives for the rest of the
      // session — the "Unsaved" badge stays lit beside "Saved.", and
      // `isInheritedNamePattern` keeps reporting false for a row that is now
      // saved. Reached only when `newRowErrors` is empty (the check above
      // returns early otherwise), so every dirty row really was persisted.
      setDraft((d) => {
        const next = { ...d }
        for (const a of presentAgents) {
          if (next[a]?.dirty) next[a] = { ...next[a], dirty: false }
        }
        return next
      })
      setSavedAt(Date.now())
      await onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent models</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4" aria-label="agent-models">
          <p className="text-xs text-muted-foreground">
            Pick a model from the model catalog or leave the current selection in place. Temperature is in{' '}
            <code>[0, 2]</code>; clear the field to defer to the env cascade.
          </p>
          {/* Own container so the gap between agents is independent of the
              form's `space-y-4` — closed rows read as a list at 8px, where
              16px made seven headers look like seven unrelated cards. */}
          <div className="space-y-2">
          {AGENTS.map((agent) => {
            const r = resolved.agentModels[agent]
            const row = draft[agent]
            // Two ways a row has nothing to draw, and they want the same
            // treatment. `!r` is the deployment window: this backend has never
            // heard of the agent. `!row` is the single frame after one appears,
            // before the hydration effect above has seeded its draft. Rendering
            // the disabled row covers both without a second branch.
            if (!r || !row) return <UnavailableAgentRow key={agent} agent={agent} />
            const options = optionsForAgent(agent)
            const npInherited = isInheritedNamePattern(agent)
            const tempInherited = isInheritedTemperature(agent)
            const rowError = rowErrors[agent]
            const targetId = agentTargetId(agent)
            const isOpen = openAgents.has(agent)
            const bodyId = `${agent}-settings`
            return (
              <div key={agent} className="border rounded-lg overflow-hidden">
                <AgentRowHeader
                  agent={agent}
                  open={isOpen}
                  bodyId={bodyId}
                  // The same expression the badges below use, so the summary
                  // and the row it summarises can never name different models.
                  modelSpec={row.namePattern || r.model.spec}
                  temperature={row.temperature}
                  costCategory={modelCostCategory(row.namePattern || r.model.spec, pricingRows)}
                  dirty={row.dirty}
                  target={statusByTarget.get(targetId)}
                  checking={perRowChecking[targetId] ?? false}
                  onToggle={() => toggleAgent(agent)}
                />
                {isOpen && (
              <div id={bodyId} className="border-t p-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor={`${agent}-namePattern`} className="text-xs text-muted-foreground">
                      Provider:model{npInherited ? ' (inherited)' : ''}
                    </Label>
                    {/* Searchable combobox — typing narrows the catalog
                        (provider + model substring), ↑/↓/Enter to pick,
                        Esc closes. The CLEAR_OPTION_VALUE sentinel is
                        prepended as a synthetic ungrouped option so the
                        operator can still "cascade to env" from the
                        same picker without a separate clear button. */}
                    <SearchableSelect
                      id={`${agent}-namePattern`}
                      aria-label={`${agent} model`}
                      value={row.namePattern}
                      onChange={(next) =>
                        setDraft((d) => ({
                          ...d,
                          [agent]: { ...d[agent], namePattern: next, dirty: true },
                        }))
                      }
                      options={[
                        { value: CLEAR_OPTION_VALUE, label: '— (cascade to env)' },
                        ...options,
                      ]}
                      groups={providerGroups}
                      facets={modelFacetsForAgent(agent, requiredCapsFor(agent))}
                      defaultFacet={FACET_CURATED}
                      placeholder="Select a model"
                      searchPlaceholder="Search models…"
                      disabled={submitting}
                      className={npInherited ? 'italic text-muted-foreground' : ''}
                    />
                    <Provenance label="Resolved" source={modelChainSourceToProvenance(r.model.source)}>
                      {r.model.spec}
                    </Provenance>
                    {/* Price-assigned indicator (#xxx) — early warning at
                        the assignment seam. Checks against the same
                        catalog the dropdown is sourced from; an unpriced
                        row contributes $0 to the cost rollup until rates
                        are filled in on the Pricing page. */}
                    {(() => {
                      const status = modelPriceStatus(row.namePattern || r.model.spec, pricingRows)
                      if (status === 'unknown') return null
                      return status === 'priced' ? (
                        <Badge variant="secondary" className="w-fit text-[10px]">
                          ✓ priced
                        </Badge>
                      ) : (
                        <Badge variant="warning" className="w-fit text-[10px]">
                          ⚠ unpriced — see Pricing page
                        </Badge>
                      )
                    })()}
                    {/* Capability hint (#1844 Slice 1). Shown only when the
                        catalog affirmatively marks the model image-capable
                        (imageInput, #1842); a text-only model shows nothing.
                        Informational — it never disables the option, since an
                        operator may deliberately pick a cheaper text model. */}
                    {modelReadsImages(row.namePattern || r.model.spec, pricingRows) && (
                      <Badge variant="secondary" className="w-fit text-[10px]">
                        👁 vision enabled
                      </Badge>
                    )}
                    {/* #1928 — the same three-state capability badges the
                        dropdown options carry, on the SELECTED model. They are
                        load-bearing rather than decorative: `Otto Curated`
                        remains the default view and is not capability-filtered,
                        so on the screen an operator actually opens these are the
                        only capability signal present. */}
                    {(() => {
                      const capRow = rowByPattern.get(row.namePattern || r.model.spec)
                      const ctx = formatContextWindow(capRow?.maxInputTokens)
                      return (
                        <>
                          <CapabilityBadge value={capRow?.toolCalling} label="🔧 tool calling" />
                          {reasoningBadge(capRow)}
                          {ctx && (
                            <Badge variant="secondary" className="w-fit text-[10px]">{ctx}</Badge>
                          )}
                        </>
                      )
                    })()}
                    {/* Save-time warning. Reached by searching past the filter,
                        which SearchableSelect deliberately allows — so the chip
                        can never be the only guard. A warning, not a block:
                        capability data can be missing or stale, and locking an
                        operator out of a model that actually works is worse than
                        letting them proceed informed. */}
                    {modelFallsShort(agent, row.namePattern || r.model.spec) && (
                      <div className="rounded-md border border-warning bg-warning/10 p-2 text-xs">
                        ⚠{' '}
                        <b>
                          This model can't use tools, and the {agent} needs that to{' '}
                          {AGENT_PURPOSE[agent]}.
                        </b>{' '}
                        The {agent} will be skipped and your run will keep going, with a note in
                        the run log. You can still save this if you know our records are out of
                        date.
                      </div>
                    )}
                    {/* Relative cost category (#1844 Slice 2) — a Low/Medium/
                        High/Premium pill instead of a dollar figure, so this
                        selection surface stays audience-neutral (no dollars).
                        This chip shows the SELECTED model's cost at a glance;
                        each dropdown option carries the same pill so cost is
                        also visible while browsing. Absent on an unpriced model
                        (the ⚠ unpriced badge above already covers that). */}
                    {(() => {
                      const cat = modelCostCategory(row.namePattern || r.model.spec, pricingRows)
                      return cat ? <CostCategoryPill category={cat} /> : null
                    })()}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`${agent}-temperature`} className="text-xs text-muted-foreground">
                      Temperature{tempInherited ? ' (inherited)' : ''}
                    </Label>
                    {/*
                      type="text" inputMode="decimal" rather than
                      type="number": we want to validate ourselves
                      (out-of-range, NaN, empty) so the error path is
                      explicit; type="number" lets browsers silently
                      drop characters and clamp values, which would
                      mask the operator's typo instead of surfacing it.
                    */}
                    <Input
                      id={`${agent}-temperature`}
                      type="text"
                      inputMode="decimal"
                      value={row.temperature}
                      placeholder="(provider default)"
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          [agent]: { ...d[agent], temperature: e.target.value, dirty: true },
                        }))
                      }
                      disabled={submitting}
                      className={tempInherited ? 'italic text-muted-foreground' : ''}
                      aria-invalid={rowError ? 'true' : undefined}
                    />
                    <Provenance label="Resolved" source={temperatureChainSourceToProvenance(r.temperature.source)}>
                      {r.temperature.value !== null ? String(r.temperature.value) : '(provider default)'}
                    </Provenance>
                  </div>
                </div>
                {rowError && (
                  <p className="text-xs text-destructive" role="alert">
                    {rowError}
                  </p>
                )}
                {/* Advisory recommendation (#1844 Slice 3). Scored agents get
                    the full cost↔performance slider ranking curated ∪ this
                    agent's run history; the other four get a cost-only note.
                    Assign only fills the picker draft — Save still persists. */}
                {isScoredAgent(agent) ? (
                  <HelpMeChoose
                    agent={agent}
                    pricingRows={pricingRows}
                    leaderboard={leaderboards[agent]}
                    weight={sliderWeights[agent] ?? 0.5}
                    onWeight={(w) => setSliderWeights((s) => ({ ...s, [agent]: w }))}
                    onAssign={(namePattern) =>
                      setDraft((d) => ({ ...d, [agent]: { ...d[agent], namePattern, dirty: true } }))
                    }
                  />
                ) : (
                  <HelpMeChooseCostOnly />
                )}
                {/* Masked preview of the key this agent's provider uses —
                    confirms which key is configured after a rotation. */}
                <ApiKeyPreview target={statusByTarget.get(targetId)} />
                {/* This agent's live health, joined from the verification
                    list by `agent:<name>`. Hidden until the list loads
                    (or if it soft-failed) so a verification outage never
                    obscures the assignment controls above. */}
                <ModelHealth
                  target={statusByTarget.get(targetId)}
                  checking={perRowChecking[targetId] ?? false}
                  error={perRowError[targetId]}
                  onCheck={() => onCheckRow(targetId)}
                />
              </div>
                )}
              </div>
            )
          })}
          </div>

          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          {savedAt && !error && <p className="text-sm text-success" key={savedAt}>Saved.</p>}

          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save agent models'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

export default AgentModelsPage
